import { Link } from "react-router-dom";
import { ArrowRight, Download } from "lucide-react";
import ElegantPlaceholder from "@/components/ElegantPlaceholder";

const STATIONEN = [
  {
    jahr: "2025 – heute",
    titel: "Mietkoch · Privatkoch · Koch-Vermittlung",
    text: "Seit 2025 selbstständig: als Mietkoch für Gastronomiebetriebe, Hotels, Restaurants, Firmenevents und Bankette — als Privatkoch für Menü-Abende und Private Dining bei Kunden zuhause — und als Vermittler geprüfter selbstständiger Köche: an Betriebe, die kurzfristig Verstärkung oder eine Festanstellung suchen, und an Privatkunden, die einen Koch für ihren Anlass möchten.",
    image: "https://images.unsplash.com/photo-1583394293214-28ded15ee548?w=1200&q=80&auto=format&fit=crop",
  },
  {
    jahr: "2023 – 2024",
    titel: "Malus – das Restaurant",
    text: "Gründer, Geschäftsführer und Alleinkoch. Eigenes Konzept von der Küchenleitung bis zum Service-Pass — verantwortlich für Konzeption, Wareneinkauf, Karte und tägliche Umsetzung.",
    image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200&q=80&auto=format&fit=crop",
  },
  {
    jahr: "2021 – 2022",
    titel: "das Steak-Haus",
    text: "Geschäftsführer und Küchenchef. Verantwortung für Personal, Wareneinkauf, Konzeption der Karte sowie die komplette Küche im laufenden Betrieb.",
    image: "https://images.unsplash.com/photo-1546833999-b9f581a1996d?w=1200&q=80&auto=format&fit=crop",
  },
  {
    jahr: "2016 – 2021",
    titel: "Burghof – das Hotel",
    text: "Ausbildung zum Koch, anschließend Chef de Partie auf den Posten Garde-Manger und Entremetier. Nach mehreren Jahren Übernahme der Position als Sous Chef.",
    image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1200&q=80&auto=format&fit=crop",
  },
];

export default function UeberMich() {
  return (
    <div data-testid="page-ueber-mich">
      <section className="border-b border-[#E3DECE]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52]">Über mich</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter mt-6">
              Mit Leidenschaft am Herd.
            </h1>
            <p className="text-[#757067] font-light text-lg mt-8 max-w-xl leading-relaxed">
              Mein Anspruch: in jeder Küche so arbeiten, als wäre es meine eigene.
              Strukturiert, ruhig und mit einem klaren Blick fürs Wesentliche — auch
              dann, wenn der Druck steigt. Sauberkeit, Verlässlichkeit und ein
              professionelles Auftreten sind für mich keine Schlagworte, sondern
              tägliche Praxis.
            </p>
            <p className="text-[#757067] font-light text-lg mt-6 max-w-xl leading-relaxed">
              Vom À-la-carte-Service über Bankett bis zum Catering-Einsatz: Ich behalte
              den Überblick, halte die Linie sauber und übergebe meine Posten so, wie
              ich sie selbst übernehmen möchte.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative w-full aspect-[3/4] overflow-hidden border border-[#E3DECE]">
              <img
                src="/portrait.jpg"
                alt="Lukas Stutz"
                className="w-full h-full object-cover"
                data-testid="ueber-mich-portrait"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Werte */}
      <section className="border-b border-[#E3DECE] bg-[#F2EFE9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52]">Werte</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light tracking-tighter mt-6 max-w-2xl">
            Drei Grundsätze, die meine Arbeit prägen.
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              {
                nr: "01",
                titel: "Überblick unter Druck",
                text: "Auch in der heißesten Phase bleibt die Linie strukturiert. Ruhe am Pass, klare Kommunikation, durchdachte Reihenfolge.",
                image: "/images/01.jpg",
              },
              {
                nr: "02",
                titel: "Sauberkeit ohne Kompromisse",
                text: "Eine saubere Küche ist Grundvoraussetzung für gutes Essen. Mise en Place, Posten und Übergabe immer in einwandfreiem Zustand.",
                image: "/images/sauberkeit.jpg",
              },
              {
                nr: "03",
                titel: "Professionelles Auftreten",
                text: "Verlässlich, pünktlich, fokussiert. Im Team und gegenüber Gästen — diskret, freundlich und mit dem Anspruch, Qualität zu liefern.",
                image: "/images/professionell.jpg",
              },
            ].map((w) => (
              <div key={w.titel}>
                <div className="relative w-full aspect-[4/3] overflow-hidden border border-[#E3DECE] bg-[#FAFAF7] mb-6">
                  <img src={w.image} alt={w.titel} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <h3 className="font-serif text-xl sm:text-2xl font-medium text-[#2C2A26]">{w.titel}</h3>
                <p className="text-[#757067] font-light leading-relaxed mt-3">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-[#E3DECE]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52]">Werdegang</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-6 max-w-2xl">
            Stationen, die geprägt haben.
          </h2>

          <div className="mt-16 border-t border-[#E3DECE]">
            {STATIONEN.map((s, i) => (
              <div
                key={i}
                className="border-b border-[#E3DECE] py-10 grid grid-cols-12 gap-6"
              >
                <div className="col-span-12 md:col-span-2">
                  <p className="font-serif text-2xl text-[#BA6D52]">{s.jahr}</p>
                </div>
                <div className="col-span-12 md:col-span-10">
                  <h3 className="font-serif text-2xl font-medium">{s.titel}</h3>
                  <p className="text-[#757067] font-light mt-3 leading-relaxed max-w-2xl">
                    {s.text}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* CV Download */}
          <div className="mt-16 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-8 border-t border-[#E3DECE]">
            <div className="max-w-lg">
              <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">
                Vollständiger Werdegang
              </p>
              <p className="text-[#757067] font-light mt-3 leading-relaxed">
                Detaillierter Lebenslauf mit allen Stationen, Positionen und
                Kompetenzen — für Betriebe, HR &amp; Personalentscheider.
              </p>
            </div>
            <a
              href="/Lebenslauf-Lukas-Stutz.pdf"
              download="Lebenslauf-Lukas-Stutz.pdf"
              data-testid="ueber-mich-cv-download"
              className="group inline-flex items-center gap-3 border border-[#2C2A26] text-[#2C2A26] hover:bg-[#2C2A26] hover:text-[#FAFAF7] px-6 py-4 text-xs uppercase tracking-[0.2em] transition-colors self-start sm:self-auto flex-shrink-0"
            >
              <Download size={16} className="transition-transform group-hover:translate-y-0.5" />
              Lebenslauf herunterladen
            </a>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52]">Zusammenarbeit</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-6 max-w-3xl mx-auto">
            Bereit, das nächste Projekt anzugehen?
          </h2>
          <Link
            to="/anfrage"
            data-testid="ueber-mich-cta-anfrage"
            className="mt-10 inline-flex items-center gap-3 bg-[#BA6D52] text-white hover:bg-[#9E5841] px-8 py-5 text-xs uppercase tracking-[0.2em] transition-colors"
          >
            Anfrage stellen <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
